import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { apiClient } from "~/api/ApiClient";
import { uploadAndGetSignedId } from "~/api/apiFunctions";
import yourLogoHereUrl from "~/assets/images/your-logo-here.png";
import FormLabel from "~/components/login/formLabel/FormLabel";
import FormTitle from "~/components/login/formTitle/FormTitle";
import settingsStyles from "~/components/settings/Settings.module.scss";
import UploadImage from "~/components/settings/uploadImage/UploadImageControlled";
import Button from "~/components/ui/button/Button";
import FormBaseErrors from "~/components/ui/formBaseErrors/FormBaseErrors";
import Input from "~/components/ui/input/Input";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider";
import labels from "~/helpers/labels";
import { tryCatch, tryCatchParsed } from "~/helpers/tryCatch";
import { useFormErrors } from "~/hooks/useFormErrors";
import { usePageTitle } from "~/hooks/usePageTitle";
import loginStyles from "~/pages/login/Login.module.scss";

import styles from "./Onboarding.module.scss";

// The white-label account rejects GIF logos.
const LOGO_TYPES = ["image/png", "image/jpg", "image/jpeg", "image/webp"];

type FormValues = {
  name: string;
};

type PlatformPayload = {
  name: string;
  teamLogo?: string;
  tenantLogo?: string;
};

const uploadLogo = async (file: File) => {
  // Teams and white-label accounts keep their files in separate storages.
  const [teamLogo, tenantLogo] = await Promise.all([
    uploadAndGetSignedId(file, "teams", "logo"),
    uploadAndGetSignedId(file, "tenant/accounts", "logo"),
  ]);

  return { teamLogo, tenantLogo };
};

const PlatformSetupPage = () => {
  usePageTitle(PAGE_TITLES.setUpPlatform);

  const { team } = useTeam();
  const queryClient = useQueryClient();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!logoPreviewUrl) return;

    return () => URL.revokeObjectURL(logoPreviewUrl);
  }, [logoPreviewUrl]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: { name: "" } });

  const { baseErrors, applyServerErrors, clearBaseErrors } =
    useFormErrors<FormValues>(setError, ["logo"]);

  const { mutateAsync } = useMutation({
    mutationFn: async ({ name, teamLogo, tenantLogo }: PlatformPayload) => {
      await apiClient.teams.teamsPartialUpdate(team?.id ?? 0, {
        name,
        ...(teamLogo && { logo: teamLogo }),
      });
      await apiClient.tenant.accountCreate({
        name,
        ...(tenantLogo && { logo: tenantLogo }),
      });
    },
  });

  // Covers the whole submit: the logo upload, the writes and the team refetch
  // that moves the guard on (a second click would re-create the account).
  const isBusy = isSubmitting;

  // "teams" covers the current team (its refetch clears
  // platform_setup_required, so the guard moves on) and the team switcher's
  // list, which isn't mounted here: refetch inactive queries too, or the
  // switcher keeps the old name and logo.
  const refreshTeams = async () => {
    void queryClient.invalidateQueries({
      queryKey: ["tenantAccount", team?.id],
    });
    await queryClient.invalidateQueries({
      queryKey: ["teams"],
      refetchType: "all",
    });
  };

  const onSubmit = async ({ name }: FormValues) => {
    clearBaseErrors();

    let logos: Omit<PlatformPayload, "name"> = {};

    if (logoFile) {
      const upload = await tryCatch(uploadLogo(logoFile));

      if (upload.error) {
        enqueueSnackbar("Couldn't upload the logo. Please try again.", {
          variant: "error",
          persist: true,
        });

        return;
      }

      logos = upload.data;
    }

    const result = await tryCatchParsed(
      mutateAsync({ name: name.trim(), ...logos }),
    );

    if (result.error) {
      applyServerErrors(result.error);
      // A previous attempt may have created the account and only lost the
      // refetch (a retry then gets 403): reread the team so the guard can
      // move on instead of leaving the user stuck on the step.
      await refreshTeams();

      return;
    }

    await refreshTeams();
  };

  const handleLogoUpload = (file: File) => {
    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
  };

  const handleLogoRemove = () => {
    setLogoFile(null);
    setLogoPreviewUrl(null);
  };

  return (
    <>
      <FormTitle title={PAGE_TITLES.setUpPlatform} />
      <div className={loginStyles.infoText}>
        Give your platform a name and add your logo. Your clients will see them
        when they log in.
      </div>
      <FormBaseErrors errors={baseErrors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={loginStyles.inputsWrapper}>
          <FormLabel label="What do you want to call your platform?" />
          <Input
            placeholder="e.g. Acme SEO"
            errormessage={errors.name?.message ?? ""}
            {...register("name", {
              required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
              validate: (value) =>
                value.trim().length > 0 || labels.THIS_FIELD_CANNOT_BE_EMPTY,
            })}
            disabled={isBusy}
          />
        </div>
        {/* Laid out like the logo in the team settings. */}
        <div className={loginStyles.inputsWrapper}>
          <FormLabel label="Logo (optional)" />
          <p className={settingsStyles.settingsText}>
            PNG, JPG or WebP, up to 5 MB.
          </p>
          <div className={settingsStyles.settingsUpload}>
            <UploadImage
              disabled={isBusy}
              valueUrl={logoPreviewUrl}
              placeholderImageUrl={yourLogoHereUrl}
              allowedTypes={LOGO_TYPES}
              onUpload={handleLogoUpload}
              onRemove={handleLogoRemove}
            />
          </div>
        </div>
        <Button
          className={styles.submit}
          fullWidth
          type="submit"
          disabled={isBusy}
          withSpinner={isBusy}
        >
          Continue
        </Button>
      </form>
    </>
  );
};

export default PlatformSetupPage;
